import type { ReactNode } from "react"

import { useTranslation } from "react-i18next"

import { ArrowLeft, ArrowRight, type LucideIcon, X } from "@/components/icons"
import { Button } from "@/components/ui/button"

import { WizardStepIndicator } from "./wizard-step-indicator"

type WizardContainerProps = {
  title: string
  steps: { key: string; label: string; icon: LucideIcon }[]
  currentStep: number
  onNext: () => void
  onBack: () => void
  onCancel: () => void
  onStepClick?: (step: number) => void
  isNextDisabled?: boolean
  isGenerating?: boolean
  onGenerate?: () => void
  children: ReactNode
}

export function WizardContainer({
  title,
  steps,
  currentStep,
  onNext,
  onBack,
  onCancel,
  onStepClick,
  isNextDisabled = false,
  isGenerating = false,
  onGenerate,
  children,
}: WizardContainerProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.wizard" })

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{title}</h1>
        <Button size="icon" variant="ghost" onClick={onCancel}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Step indicator */}
      <div className="mx-auto w-full max-w-2xl">
        <WizardStepIndicator currentStep={currentStep} steps={steps} onStepClick={onStepClick} />
      </div>

      {/* Step content */}
      <div className="min-h-[400px]">{children}</div>

      {/* Navigation */}
      <div className="flex items-center justify-between border-t pt-4">
        <div className="flex items-center gap-2">
          {currentStep > 0 && (
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("back")}
            </Button>
          )}
          <span className="text-muted-foreground text-sm">
            {t("stepOf", { current: currentStep + 1, total: steps.length })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={onCancel}>
            {t("cancel")}
          </Button>
          <Button
            disabled={isNextDisabled || isGenerating}
            variant={currentStep === steps.length - 1 ? "default" : "outline"}
            onClick={onGenerate}
          >
            {isGenerating ? t("generating") : t("generate")}
          </Button>
          {currentStep < steps.length - 1 && (
            <Button disabled={isNextDisabled} onClick={onNext}>
              {t("next")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
